import { NgTemplateOutlet } from '@angular/common';
import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	forwardRef,
	inject,
	Injector,
	input,
	output,
	signal,
	TemplateRef,
	untracked,
	viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { LvrInputDirective } from '../../directives/lvr-input.directive';

export interface SearchableSelectOptionContext<T> {
	$implicit: T;
	option: T;
	index: number;
	selected: boolean;
}

/**
 * Single-select autocomplete with client-side filtering.
 * Filtering happens in memory on every keystroke, no backend request is triggered.
 */
@Component({
	selector: 'lvr-searchable-select',
	templateUrl: './searchable-select.html',
	styleUrl: './searchable-select.scss',
	imports: [
		MatFormField,
		MatError,
		MatInputModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatIconModule,
		LvrInputDirective,
		NgTemplateOutlet,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LvrSearchableSelect),
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LvrSearchableSelect<T> implements ControlValueAccessor {
	public readonly options = input.required<readonly T[]>();
	/** Extracts the form value of an option. Defaults to the `id` property. */
	public readonly optionValue = input<(option: T) => unknown>((option) => (option as { id: unknown }).id);
	/** Extracts the displayed and searched text of an option. Defaults to the `remark` property. */
	public readonly optionLabel = input<(option: T) => string>((option) =>
		String((option as { remark: unknown }).remark ?? ''),
	);
	public readonly placeholder = input<string>('');
	public readonly searchPlaceholder = input<string>('');
	public readonly noResultsText = input<string>('');
	public readonly ariaLabel = input<string>('');
	public readonly required = input<boolean>(false);
	public readonly hasError = input<boolean>(false);
	public readonly errorMessage = input<string>('');
	public readonly hintText = input<string>('');
	public readonly clearAriaLabel = input<string>('Clear selection');
	public readonly optionTemplate = input<TemplateRef<SearchableSelectOptionContext<T>> | null>(null);

	public readonly searchChange = output<string>();
	public readonly scrolledToEnd = output<void>();

	public readonly selectedOption = signal<T | undefined>(undefined);
	private readonly selectedMatOption = signal<MatOption | undefined>(undefined);
	private readonly formValue = signal<unknown>(undefined);
	private lastInteraction: 'search' | 'selection' = 'selection';
	public readonly filterText = signal('');
	public readonly isOpen = signal(false);
	public readonly disabled = signal(false);
	public readonly value = computed(() =>
		this.selectedOption() ? this.optionValue()(this.selectedOption()!) : undefined,
	);
	public readonly searchTerm = computed(() => (this.isOpen() ? this.filterText() : this.displayValue()));

	public readonly filteredOptions = computed(() => {
		const term = this.filterText().trim().toLowerCase();
		const options = this.options();
		if (!term) return [...options];
		const label = this.optionLabel();
		return options.filter((option) => label(option).toLowerCase().includes(term));
	});

	private readonly matInput = viewChild.required(MatInput);
	private readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);
	private readonly autocomplete = viewChild(MatAutocomplete);
	private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
	private readonly injector = inject(Injector);

	public constructor() {
		effect(() => {
			const value = this.formValue();
			const option = this.options().find((candidate) => this.optionValue()(candidate) === value);
			this.selectedOption.set(option);

			if (!this.isOpen()) {
				this.filterText.set(option ? this.optionLabel()(option) : '');
			}
		});

		effect(() => {
			const error = this.hasError() && !this.isOpen();

			this.matInput().errorState = error;
			this.matInput().stateChanges.next();
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onChange: (value: unknown) => void = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	public onTouched: () => void = () => {};

	public onSearch(event: Event): void {
		const searchTerm = (event.target as HTMLInputElement).value;

		this.filterText.set(searchTerm);
		this.searchChange.emit(searchTerm);

		this.lastInteraction = 'search';

		afterNextRender(() => this.restoreCommittedOption(), { injector: this.injector });

		this.isOpen.set(true);
		this.autocompleteTrigger()?.openPanel();
	}

	public openPanel(): void {
		if (this.isOpen()) return;
		this.filterText.set('');
		this.isOpen.set(true);
		this.autocompleteTrigger()?.openPanel();
		afterNextRender(() => this.restoreCommittedOption(), { injector: this.injector });
	}

	public onOptionSelected(value: unknown, input?: HTMLInputElement, matOption?: MatOption): void {
		const option = this.resolveOption(value);
		if (!option) return;
		const label = this.optionLabel()(option);
		this.selectedOption.set(option);
		this.selectedMatOption.set(matOption);
		this.formValue.set(this.optionValue()(option));
		this.lastInteraction = 'selection';
		this.filterText.set('');
		this.isOpen.set(false);
		this.onChange(this.optionValue()(option));
		if (input) input.value = label;
	}

	public onBlur(): void {
		this.onTouched();
	}

	public displayValue(): string {
		const option = this.selectedOption();
		return option ? this.optionLabel()(option) : '';
	}

	public displayValueForValue(value: unknown): string {
		const option = this.resolveOption(value);
		return option ? this.optionLabel()(option) : '';
	}

	private resolveOption(value: unknown): T | undefined {
		return this.options().find((option) => option === value || this.optionValue()(option) === value);
	}

	public onPanelClosed(): void {
		this.isOpen.set(false);
		this.onTouched();
		if (this.lastInteraction === 'selection') {
			this.filterText.set(this.displayValue());
		}
	}

	private restoreCommittedOption(): void {
		this.committedMatOption()?.select(false);
	}

	/** Resolves the rendered option for the committed value, including values written by a parent form. */
	private committedMatOption(): MatOption | undefined {
		const selectedValue = this.value();
		if (selectedValue === undefined) return undefined;
		return this.autocomplete()?.options.find((candidate) => candidate.value === selectedValue);
	}

	public clear(input?: HTMLInputElement): void {
		const wasOpen = this.isOpen();
		if (input) {
			input.value = '';
			if (wasOpen) input.dispatchEvent(new Event('input', { bubbles: true }));
		}

		if (wasOpen) {
			this.filterText.set('');
			return;
		}

		(this.selectedMatOption() ?? this.committedMatOption())?.deselect();
		this.selectedMatOption.set(undefined);
		this.selectedOption.set(undefined);
		this.formValue.set(undefined);
		this.filterText.set('');
		this.lastInteraction = 'selection';
		this.onChange(undefined);
		this.onTouched();
	}

	public writeValue(value: unknown): void {
		// Form writes inside a parent effect must not subscribe that effect to this control's options.
		untracked(() => {
			this.formValue.set(value);
			const selectedOption = this.options().find((option) => this.optionValue()(option) === value);
			this.selectedOption.set(selectedOption);
			this.filterText.set(selectedOption ? this.optionLabel()(selectedOption) : '');
			this.lastInteraction = 'selection';
		});
	}

	public registerOnChange(fn: (value: unknown) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled);
	}

	protected onAutocompleteOpened(): void {
		afterNextRender(
			{
				read: () => {
					const panel = this.autocomplete()?.panel?.nativeElement;

					if (!panel) return;

					panel.removeEventListener('scroll', this.onPanelScroll);
					panel.addEventListener('scroll', this.onPanelScroll);
				},
			},
			{ injector: this.injector },
		);
	}

	private readonly onPanelScroll = (event: Event): void => {
		const panel = event.target as HTMLElement;
		const threshold = 50;

		const reachedBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - threshold;

		if (reachedBottom) {
			this.scrolledToEnd.emit();
		}
	};
}
